<script setup lang="ts">
/**
 * AppNav — Primary navigation component.
 *
 * Renders two states:
 *   GUEST:       [Logo] [APP_NAME] | Builder | Sign In
 *   AUTHENTICATED: [Logo] [APP_NAME] | Builder | My Campaigns | [AB ▼]
 *
 * AvatarMenu (initials dropdown) appears only when authenticated. It contains
 * the account actions and, on a phone (where the header hides the app links), the app links too:
 * Builder and My Campaigns (phone only), Game codes (maintainer only), Send feedback, Privacy,
 * Logout. No builder actions (import/export/save/etc.) live
 * here — those belong in the builder toolbar.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { APP_NAME, FEEDBACK_EMAIL } from '@/data/app'
import { useAuth } from '@/composables/useAuth'
import { useAccess } from '@/composables/useAccess'
import { useTheme } from '@/composables/useTheme'
import BrandWordmark from '@/components/BrandWordmark.vue'
import { isPreLaunch } from '@/utils/prelaunch'

const { t } = useI18n()
const { user, signOut } = useAuth()
const { isMaintainer, loadAccess } = useAccess()
const { theme, toggleTheme } = useTheme()
const router = useRouter()

// A signed-in access holder sees the builder/campaigns links on any page (ADR-099).
watch(
  user,
  (u) => {
    if (u) loadAccess()
  },
  { immediate: true },
)

/* ─── Avatar calculations ─── */
const initials = computed(() => {
  const email = user.value?.email ?? ''
  if (!email) return '?'

  // Extract local-part (before @)
  const atIndex = email.indexOf('@')
  const local = atIndex >= 0 ? email.slice(0, atIndex) : email
  if (!local) return '?'

  // If local-part contains a separator, take first letter of first and last word
  const separatorMatch = local.match(/^([a-zA-Z])[^._-]*[._-]+[^._-]*?([a-zA-Z])/)
  if (separatorMatch?.[1] && separatorMatch[2])
    return (separatorMatch[1] + separatorMatch[2]).toUpperCase()

  // Otherwise: first letter only
  return local.charAt(0).toUpperCase()
})

/*
 * Deterministic per-user avatar hues (a stable colour derived from the email)
 * are RESERVED for future in-campaign player identification — where telling
 * players apart at a glance earns the palette exception. They are intentionally
 * NOT used here: the primary-nav avatar stays on token-based styling so it reads
 * as a neutral account affordance, not a coloured identity chip. Do not
 * reintroduce a per-user background on this avatar (ADR-008: no colour literals).
 */

/* ─── Dropdown state ─── */
const dropdownOpen = ref(false)
const dropdownRoot = ref<HTMLElement | null>(null)

function toggleDropdown() {
  dropdownOpen.value = !dropdownOpen.value
}

function closeDropdown() {
  dropdownOpen.value = false
}

async function handleSignOut() {
  closeDropdown()
  await signOut()
  router.push({ name: 'builder' })
}

function handleClickOutside(event: MouseEvent) {
  if (
    dropdownOpen.value &&
    dropdownRoot.value &&
    !dropdownRoot.value.contains(event.target as Node)
  ) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <nav aria-label="Primary" class="flex items-center justify-between">
    <!-- Left: brand + nav links -->
    <div class="flex items-center gap-6">
      <!--
        Brand / logo. Currently points to "/" (the landing). When already on the
        landing, this is a self-link (no-op). The route guard handles this.
      -->
      <router-link
        to="/"
        class="inline-flex items-center rounded hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        <BrandWordmark :alt="APP_NAME" class="h-7 w-auto" />
      </router-link>

      <!-- Pre-launch (ADR-057): the router sends builder and campaigns home, so no links to them — except for a signed-in account (ADR-099 D2 as amended). -->
      <div v-if="!isPreLaunch || user" class="hidden items-center gap-4 sm:flex">
        <!-- Builder — always visible -->
        <router-link
          :to="{ name: 'builder' }"
          class="text-sm font-medium text-fg-primary transition-colors hover:text-accent-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {{ t('nav.builder') }}
        </router-link>

        <!--
          "My Campaigns" — visible ONLY when authenticated.
          The campaigns link is PRIMARY navigation, not buried in a settings menu.
        -->
        <router-link
          v-if="user"
          :to="{ name: 'campaigns' }"
          class="text-sm font-medium text-fg-primary transition-colors hover:text-accent-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          {{ t('nav.campaigns') }}
        </router-link>
      </div>
    </div>

    <!-- Right: guest sign-in / authenticated avatar -->
    <div class="flex items-center gap-3">
      <!-- raw button: icon-only theme toggle — not expressible as AppButton -->
      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <button
        type="button"
        class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        :aria-label="theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')"
        :title="theme === 'dark' ? t('nav.themeLight') : t('nav.themeDark')"
        @click="toggleTheme"
      >
        <svg
          v-if="theme === 'light'"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-5 w-5"
          aria-hidden="true"
        >
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="h-5 w-5"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="4" />
          <path
            d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"
          />
        </svg>
      </button>

      <!-- GUEST state -->
      <!--
        Sign In → /auth?redirect=/builder.
        This is a generic "log in" CTA. The user may be on any page. After auth
        they return to the builder. This differs from the hero "Run a Campaign"
        CTA which sends to /auth?redirect=/campaigns — they MUST remain separate.
      -->
      <router-link
        v-if="!user"
        :to="{ name: 'auth', query: { redirect: '/builder' } }"
        class="rounded-lg border border-border px-4 py-2 text-sm font-semibold text-fg-primary transition-all hover:border-border-strong hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {{ t('nav.signIn') }}
      </router-link>

      <!-- AUTH state: avatar initials dropdown -->
      <div v-else ref="dropdownRoot" class="relative">
        <!--
          The redirect query param must be sanitised wherever it's consumed
          (AuthView, useAuth). See sanitizeRedirect in LandingView.vue for the
          reference implementation.

          @click.stop prevents the click from bubbling up to the document-level
          handleClickOutside listener. The toggle still fires.
        -->
        <!-- raw button: circular avatar with token-based ground + initials text — not expressible as AppButton -->
        <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
        <button
          type="button"
          class="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-surface-raised text-sm font-bold text-fg-primary ring-2 ring-transparent transition-all hover:ring-accent-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          aria-haspopup="menu"
          :aria-expanded="dropdownOpen"
          aria-label="Account menu"
          @click.stop="toggleDropdown"
        >
          {{ initials }}
        </button>

        <!-- Dropdown: account-only actions -->
        <Transition
          enter-active-class="transition-opacity duration-100"
          leave-active-class="transition-opacity duration-100"
          enter-from-class="opacity-0"
          leave-to-class="opacity-0"
        >
          <div
            v-if="dropdownOpen"
            role="menu"
            class="absolute right-0 z-20 mt-2 w-48 rounded-lg border border-border bg-surface py-1 shadow-xl"
          >
            <div class="border-b border-border px-4 py-2 text-xs text-fg-secondary truncate">
              {{ user.email }}
            </div>

            <!-- Phone (< sm): the header hides the app links, so the menu carries them (post-INC-187, R-7). -->
            <div v-if="!isPreLaunch || user" class="sm:hidden">
              <router-link
                :to="{ name: 'builder' }"
                role="menuitem"
                class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                @click="closeDropdown"
                >{{ t('nav.builder') }}</router-link
              >
              <router-link
                :to="{ name: 'campaigns' }"
                role="menuitem"
                class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                @click="closeDropdown"
                >{{ t('nav.campaigns') }}</router-link
              >
              <div class="my-1 h-px bg-border"></div>
            </div>

            <router-link
              v-if="isMaintainer"
              to="/pack-codes"
              role="menuitem"
              class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              @click="closeDropdown"
            >
              {{ t('nav.packCodes') }}
            </router-link>
            <a
              :href="`mailto:${FEEDBACK_EMAIL}`"
              role="menuitem"
              class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              @click="closeDropdown"
            >
              {{ t('nav.feedback') }}
            </a>
            <router-link
              :to="{ name: 'help' }"
              role="menuitem"
              class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              @click="closeDropdown"
            >
              {{ t('nav.help') }}
            </router-link>
            <router-link
              :to="{ name: 'privacy' }"
              role="menuitem"
              class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              @click="closeDropdown"
            >
              {{ t('nav.privacy') }}
            </router-link>

            <div class="my-1 h-px bg-border"></div>

            <!-- raw button: dropdown menuitem primitive -->
            <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
            <button
              type="button"
              role="menuitem"
              class="flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-sm text-fg-primary transition-colors hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              @click="handleSignOut"
            >
              {{ t('nav.signOut') }}
            </button>
          </div>
        </Transition>
      </div>
    </div>
  </nav>
</template>

